// filepath: apps/web/src/admin/tenants/TenantAdminGuard.tsx
//
// FR-TEN-6 / ISS-050: permission guard wrapper for admin
// de-provisioning routes. The SPA operator admin endpoints
// (`/admin/tenants/:tenantId/deprovision`) are NOT behind a
// per-tenant JWT — the platform operator token authorizes the
// cross-tenant administrative action. The guard therefore checks
// for the presence of the operator token and renders a
// permission-denied UI when it is missing.
//
// This component is intentionally simple: the actual server-side
// authorization (StaticOperatorAuth, env-var PLATFORM_ADMIN_TOKEN)
// is the source of truth. The client-side guard exists to give
// the user a clear UX when the token is not configured — not for
// security (an attacker can trivially bypass it).
//
// SECURITY: never render tenant data inside this guard. The
// children prop is only mounted when the operator token is present.

import React from "react";

export interface TenantAdminGuardProps {
  children: React.ReactNode;
  /** Override the operator-token probe (for tests). */
  hasOperatorToken?: boolean;
}

function probeOperatorToken(): boolean {
  if (typeof window === "undefined") return false;
  // The platform operator token is injected at bootstrap by the
  // operator admin app shell. Tests can pass it via window globals.
  const w = window as unknown as { __PLATFORM_ADMIN_TOKEN__?: string };
  return Boolean(w.__PLATFORM_ADMIN_TOKEN__);
}

export function TenantAdminGuard({
  children,
  hasOperatorToken,
}: TenantAdminGuardProps): React.ReactElement {
  const present = hasOperatorToken ?? probeOperatorToken();
  if (!present) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        data-testid="admin-guard-denied"
        className="admin-guard-denied"
      >
        <h2>Operator credentials required</h2>
        <p>
          The de-provisioning interface is only available to platform operators.
          Configure <code>PLATFORM_ADMIN_TOKEN</code> at the platform boundary
          (out of band) and reload this page.
        </p>
        <p>
          This is a <strong>destructive</strong> administrative action. There is
          no per-tenant user role that grants de-provisioning access.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}
